<template lang="pug">
  b-container.px-3.px-md-4.py-3(fluid)
    div.d-flex.justify-content-between.mb-2
      div.device-header
        span {{$t('mdm_page.device_policy')}} ({{filteredProfiles.length}})
    div.list-toolbar.mb-2
      b-form-input.list-toolbar__search(
        v-model="searchKeyword",
        :placeholder="$t('mdm_page.config_name_search')",
        size="sm",
        @keyup.enter="page.current = 1")
      //- v1 게이트: Android만 노출 → 전체 OS 필터 드롭다운 숨김. 되살리려면 주석 해제 + 아래 osFilter 기본값 "" 로
      //- b-form-select.list-toolbar__filter(
      //-   v-model="osFilter",
      //-   :options="osFilterOptions",
      //-   size="sm")
      b-btn(size="sm", variant="secondary", @click="page.current = 1") {{$t('search')}}
      b-btn(size="sm", variant="primary", @click="openCreate")
        icon(name="plus")
        span.ml-1 {{$t('mdm_page.add_config')}}
    b-card.table-card
      b-table.list-stacked(
        :fields="fields",
        :items="pagedProfiles",
        show-empty,
        :empty-text="$t('mdm_page.no_config')",
        hover,
        fixed,
        stacked="md")
        template(v-slot:cell(name)="row")
          b-btn.p-0(variant="link", @click="openDetail(row.item)") {{row.item.name}}
        template(v-slot:cell(description)="row")
          span(v-if="row.item.description") {{row.item.description}}
          span.text-muted(v-else) -
        template(v-slot:cell(actions)="row")
          b-btn(size="sm", variant="link", @click="openEdit(row.item)") {{$t('edit')}}
          //- 복사·삭제는 기본 구성 제외 (기본 구성은 편집만 가능)
          template(v-if="!row.item.is_default")
            span.text-muted ·
            b-btn(size="sm", variant="link", @click="copyProfile(row.item)") {{$t('mdm_page.copy')}}
            span.text-muted ·
            b-btn(size="sm", variant="link", @click="removeProfile(row.item)") {{$t('delete')}}
    div.pagination-wrap
      b-form-select(size="sm", v-model="page.per", :options="page.perOptions", style="width: auto")
      b-pagination.m-0(size="sm", v-model="page.current", :total-rows="filteredProfiles.length", :per-page="page.per")
    mdm-profile-modal(ref="profileModal", @saved="fetchProfiles")
</template>

<script>
// 기기정책(구성) 목록 — 회사 전체 구성을 받아 클라 검색/슬라이스
import _ from "lodash"
import { MdmProfileAPI } from "@/api/mdm"
import MdmProfileModal from "./MdmProfileModal.vue"

// 이름이 같을 때의 OS 정렬 우선순위 — android 먼저, 그다음 windows·macOS
const OS_SORT_ORDER = { android: 0, windows: 1, osx: 2 }

export default {
  name: "MdmProfilePage",
  components: { "mdm-profile-modal": MdmProfileModal },
  data() {
    return {
      profiles: [],
      searchKeyword: "",
      osFilter: "android", // v1 게이트: Android만 노출. 원복: "" (전체 OS, 드롭다운 필터·검색과 AND 결합)
      page: { current: 1, per: 20, perOptions: [10, 20, 50] },
      osFilterOptions: [
        { value: "", text: this.$t("mdm_page.all_os") },
        { value: "android", text: "Android" },
        // v1 게이트: Android만. 드롭다운 되살릴 때 함께 주석 해제
        // { value: "windows", text: "Windows" },
        // { value: "osx", text: "macOS" },
      ],
      // 너비는 % 비율 (fixed 테이블이라 정확히 분배) — 이름·설명 넓게, 액션 중간, OS 좁게
      fields: [
        { key: "name", label: this.$t("mdm_page.config_name"), sortable: true, thStyle: { width: "30%" } },
        { key: "description", label: this.$t("mdm_page.description"), thStyle: { width: "33%" } },
        { key: "os_type", label: this.$t("mdm_page.os"), sortable: true, thStyle: { width: "15%" } },
        { key: "actions", label: this.$t("mdm_page.actions"), thStyle: { width: "22%" } },
      ],
    }
  },
  computed: {
    filteredProfiles() {
      let list = this.profiles
      
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toUpperCase()
        list = _.filter(list, profile => (profile.name || "").toUpperCase().includes(keyword))
      }
      
      if (this.osFilter) {
        list = _.filter(list, profile => profile.os_type === this.osFilter)
      }
      return list
    },
    pagedProfiles() {
      const offset = this.page.per * (this.page.current - 1)
      return _.slice(this.filteredProfiles, offset, offset + this.page.per)
    },
  },
  watch: {
    // 검색·필터가 바뀌면 첫 페이지로 리셋 — 안 그러면 뒷 페이지 보던 중 결과가 줄면 현재 페이지가 범위 밖이라 빈 화면이 됨
    searchKeyword() {
      this.page.current = 1
    },
    osFilter() {
      this.page.current = 1
    },
  },
  methods: {
    async fetchProfiles() {
      try {
        const response = await MdmProfileAPI.get("")
        // 정렬: ① 기본 구성 맨 위 → ② 이름순 → ③ 같은 이름이면 OS(android 먼저).
        // (컬럼 헤더 클릭 정렬 시엔 b-table이 재정렬 — 여기선 기본 진입 순서만 지정)
        this.profiles = _.orderBy(
          response.data.objects || [],
          [
            profile => (profile.is_default ? 0 : 1),
            profile => (profile.name || "").toLowerCase(),
            profile => (OS_SORT_ORDER[profile.os_type] === undefined ? 99 : OS_SORT_ORDER[profile.os_type]),
          ],
          ["asc", "asc", "asc"],
        )
      } catch (error) {
        this.profiles = []
      }
    },
    openCreate() {
      this.$refs.profileModal.open("create")
    },
    openDetail(profile) {
      // 이름 클릭 → 읽기전용 상세(view). 상세 모달 안에서 "수정" 버튼으로 편집 전환
      this.$refs.profileModal.open("view", profile)
    },
    openEdit(profile) {
      // 액션 "편집" → 바로 편집 모드로 오픈 (view 거치지 않음)
      this.$refs.profileModal.open("edit", profile)
    },
    copyProfile(profile) {
      const dialogParams = {
        context: {
          title: this.$t("mdm_page.copy_config"),
          path: "mdm_page.confirm_copy",
          args: { name: profile.name },
        },
        withoutInput: true,
        submit: { size: "sm", color: "primary", text: "mdm_page.copy" },
      }
      this.AppState.$emit("show-confirm-dialog", dialogParams, async action => {
        if (action !== "submit") return
        try {
          await MdmProfileAPI.post(`${profile.pk}/copy`)
          this.$awn.success(this.$t("mdm_page.config_copied"))
          await this.fetchProfiles()
        } catch (error) {
          // 에러 토스트는 전역 인터셉터가 처리
        }
      })
    },
    removeProfile(profile) {
      const dialogParams = {
        context: {
          title: this.$t("mdm_page.delete_config"),
          path: "mdm_page.confirm_delete",
          args: { name: profile.name },
        },
        withoutInput: true,
        submit: { size: "sm", color: "danger", text: "delete" },
      }
      this.AppState.$emit("show-confirm-dialog", dialogParams, async action => {
        if (action !== "submit") return
        try {
          await MdmProfileAPI.delete(`${profile.pk}`)
          this.$awn.success(this.$t("mdm_page.config_deleted"))
          await this.fetchProfiles()
        } catch (error) {
          // 할당된 기기/기본 구성이면 409 — 전역 인터셉터가 처리
        }
      })
    },
  },
  created() {
    void this.fetchProfiles()
  },
}
</script>

<style scoped lang="scss">
.device-header {
  padding-bottom: 0.5rem;
  font-size: 1.2rem;
  font-weight: 500;
  color: #000000;
}
</style>
